import { env } from '../config/env';

/**
 * Maximum chat message length, mirrored from the API's LoanChatController.MaxMessageLength.
 */
export const MAX_MESSAGE_LENGTH = 4000;

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const jsonHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...authHeaders(token),
});

/**
 * Parses a single SSE event block (lines separated by '\n') into { eventType, data }.
 * Exported for unit testing.
 */
export function parseSseEvent(block) {
  const lines = block.split('\n');
  let eventType = 'message';
  let data = '';

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      data = line.slice(6);
    }
  }

  return { eventType, data };
}

/**
 * Reads an SSE response body stream and invokes onEvent(eventType, decodedData)
 * for every event. Data payloads are JSON-decoded when possible (the API JSON-encodes
 * values so newlines survive SSE transport). The terminal "[DONE]" marker is skipped.
 * Exported for unit testing with mock streams.
 */
export async function readSseStream(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      const { eventType, data } = parseSseEvent(part);
      if (data === '[DONE]') continue;

      let decoded = data;
      try {
        decoded = JSON.parse(data);
      } catch {
        // use raw if not valid JSON
      }

      if (decoded !== '' && decoded != null) {
        onEvent(eventType, decoded);
      }
    }
  }
}

async function throwResponseError(response, fallbackMessage) {
  const errorData = await response.json().catch(() => null);
  throw new Error(errorData?.message || `${fallbackMessage} (${response.status})`);
}

async function consumeChatStream(response, { onContent, onStatus, onMessageId }) {
  if (!response.ok) {
    await throwResponseError(response, 'Request failed');
  }

  await readSseStream(response.body, (eventType, data) => {
    if (eventType === 'status') {
      onStatus?.(data);
    } else if (eventType === 'message-id') {
      const messageId = parseInt(data, 10);
      if (!Number.isNaN(messageId)) onMessageId?.(messageId);
    } else {
      onContent?.(data);
    }
  });
}

/**
 * Streams an AI chat reply over SSE. Invokes the handlers as events arrive.
 */
export async function streamChat({ loanId, message, intent, token, signal, onContent, onStatus, onMessageId }) {
  const response = await fetch(`${env.apiUrl}/loan-chat`, {
    method: 'POST',
    headers: {
      ...jsonHeaders(token),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ loanId, message, intent: intent || undefined }),
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

/**
 * Re-streams a fresh reply for the conversation's most recent user message.
 */
export async function regenerateChat({ loanId, token, signal, onContent, onStatus, onMessageId }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}/regenerate`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      Accept: 'text/event-stream',
    },
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

export async function getChatHistory(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to load chat history');
  return response.json();
}

export async function getChatPresets(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}/presets`, {
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to load presets');
  return response.json();
}

export async function clearChatConversation(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to clear conversation');
}

export async function runChatScenario({ loanId, scenarioType, value, token }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/scenario`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ loanId, scenarioType, value }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to run scenario');
  return response.json();
}

export async function postChatTelemetry({ loanId, eventType, intent, presetKey, conversationKey, metadata, token }) {
  await fetch(`${env.apiUrl}/loan-chat/telemetry`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({
      loanId,
      eventType,
      intent: intent || undefined,
      presetKey: presetKey || undefined,
      conversationKey: conversationKey || undefined,
      metadata: metadata || {},
    }),
  });
}

export async function postChatFeedback({ loanId, conversationKey, messageId, rating, token }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/feedback`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({
      loanId,
      conversationKey: conversationKey || undefined,
      messageId: messageId || undefined,
      rating,
    }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to submit feedback');
}
